// Org endpoints (13 §Endpoints, Company): company, people, roles, seats, digital employees,
// ingest keys, external identities; M1-02: teams, org chart, responsibilities, recurring work,
// templates, webhooks (04 §Org, §Events and webhooks).
import { z } from "zod";
import { DeJobReporting, DeReportingDuty } from "../de-config-rows";
import {
  Band,
  CompanyRelation,
  Currency,
  DeployPath,
  DigitalEmployeeStatus,
  GoalKind,
  GoalState,
  GoalTrack,
  IngestKeyScope,
  InvitationRole,
  KpiGroup,
  KpiPrincipalKind,
  KpiScope,
  MembershipKind,
  MembershipStatus,
  MetricUnit,
  PeriodType,
  PersonRole,
  RecurringWorkStatus,
  ResponsibilityStatus,
  RuntimeRefSource,
  ScoreState,
  SeatAssignmentCapacity,
  SeatDeFill,
  SeatEdgeKind,
  SeatStatus,
  TrackingMode,
  WebhookStatus,
  WorkforceAgentReportsToKind,
  WorkforceAgentStatus,
} from "../enums";
import { EventType } from "../events";
import { ExternalSubject, IngestAttrs, MeterNamespace } from "../ingest";
import {
  BoundedJson,
  boundedRecord,
  Decimal,
  DecimalInput,
  DottedName,
  Email,
  FreeText,
  IsoDate,
  LIMITS,
  NonEmpty,
  PeriodKey,
  periodPairRule,
  Timestamp,
  Uuid,
} from "../primitives";
import {
  AgentKey,
  ChartCompletenessItem,
  ChartDimension,
  ChartGatedDimension,
  ChartLens,
  ChartLensBand,
  ChartVisibilityLevel,
  Company,
  CompanySettings,
  CostEvent,
  DigitalEmployee,
  Goal,
  GoalMilestone,
  Invitation,
  LitellmProvisioningState,
  Membership,
  OrgCalendarConfig,
  RecurringWork,
  Responsibility,
  Role,
  RuntimeRef,
  Seat,
  SeatAssignment,
  SeatEdge,
  SeatEdgePolicy,
  SeatMetric,
  TaskTemplate,
  Team,
  TeamMember,
  Template,
  TemplateSpec,
  Webhook,
  WebhookDelivery,
  WebhookFilter,
  WorkerRuntime,
} from "../rows";
import { MetricBands, MetricHealth, Scorecard } from "../scorecard";
import { AgentSpecChanges } from "./agent-spec";
import {
  CompanyItemParams,
  CompanyParams,
  ImportResult,
  ImportUpload,
  PageQuery,
  page,
} from "./common";
import { DeTaskLimitsPut, DeTaskLimitsView } from "./provisioning";
import { defineRoute } from "./route";

/**
 * `companies.get`: the company, and how this reader sees money (WALLET-P1): `wallet_view` is true
 * for a client of a company whose wallet is on or ever was, who sees dollars only (no model names,
 * no provider cost; token counts only in a wallet entry's detail).
 */
export const CompanyDetail = Company.extend({ wallet_view: z.boolean() }).meta({
  id: "CompanyDetail",
});

const base = "/v1/companies/{co}";

// Company ---------------------------------------------------------------------------------------

export const CompanyPatch = z
  .strictObject({
    name: NonEmpty.optional(),
    settings: CompanySettings.optional(),
    calendar_config: OrgCalendarConfig.optional(),
    reporting_currency: Currency.optional(),
    /**
     * REPORTS-UI: the Friday report opt-out list (`settings.friday_report.opted_out_membership_ids`)
     * as the caller last read it. Members change their own entry (`reports.setSubscription`), so
     * a patch changes the stored list only with this expectation, and only when it still matches
     * (409 `conflict` otherwise); a patch whose list differs without it keeps the stored list.
     */
    opted_out_expected: z.array(Uuid).max(500).optional(),
  })
  .meta({ id: "CompanyPatch" });

// People ----------------------------------------------------------------------------------------

/** A person membership with its email (from `cp.user`) and company relations. */
export const Person = Membership.extend({
  email: Email.nullable(),
  roles: z.array(CompanyRelation),
}).meta({ id: "Person" });

/**
 * A pending membership: no access until the invitee accepts with a verified email. Who may
 * invite which relation: `owner` — an owner, or an agency operator while the company has no
 * active owner (re-issuing the creation hand-off); `admin` — an owner; `member` and
 * `client_viewer` — any manager (05 §Roles over 13's "manager"; services/api README conflicts).
 * `roles` defaults to `["member"]` (MEMBER-ROLE: a new teammate is a plain member).
 */
export const PersonInvite = z
  .strictObject({
    email: Email,
    display_name: NonEmpty,
    roles: z.array(InvitationRole).min(1).max(4).default(["member"]),
  })
  .meta({ id: "PersonInvite" });

/** The invite token from the invitation email (`{company id}.{secret}`). */
export const InvitationAccept = z
  .strictObject({ token: z.string().min(1).max(200) })
  .meta({ id: "InvitationAccept" });

export const InvitationAccepted = z
  .object({ company_id: Uuid, membership: Membership, roles: z.array(CompanyRelation) })
  .meta({ id: "InvitationAccepted" });

export const PersonPatch = z
  .strictObject({
    display_name: NonEmpty.optional(),
    status: MembershipStatus.optional(),
    roles: z.array(PersonRole).max(4).optional(),
  })
  .meta({ id: "PersonPatch" });

// Roles -----------------------------------------------------------------------------------------

export const RoleCreate = z
  .strictObject({
    name: NonEmpty,
    description: FreeText.nullable().optional(),
    default_metric_keys: z.array(z.string().max(LIMITS.key)).max(LIMITS.list).optional(),
  })
  .meta({ id: "RoleCreate" });
export const RolePatch = RoleCreate.partial().meta({ id: "RolePatch" });

// Seats -----------------------------------------------------------------------------------------

export const SeatCreate = z
  .strictObject({
    role_id: Uuid,
    title: NonEmpty,
    supervisor_seat_id: Uuid.nullable().optional(),
    staff: z.boolean().optional().describe("A staff (assistant) seat; needs a supervisor"),
    settings: BoundedJson.optional(),
  })
  .meta({ id: "SeatCreate" });

/** Closing goes through `POST /seats/{id}/close`; `proposed` is never written in the slice. */
export const SeatPatch = z
  .strictObject({
    role_id: Uuid.optional(),
    title: NonEmpty.optional(),
    supervisor_seat_id: Uuid.nullable().optional(),
    staff: z
      .boolean()
      .optional()
      .describe("A staff (assistant) seat; needs a supervisor, and is cleared with it"),
    status: SeatStatus.exclude(["closed", "proposed"]).optional(),
    settings: BoundedJson.optional(),
    de_fill: SeatDeFill.optional().describe(
      "DE-SEAT-OWNER (flag `de_seat_owner`): assist · act_when_vacant · own. `own` refuses while a person holds the seat and sets settings.human_required = false; the DE's capacity follows in the same write",
    ),
  })
  .meta({ id: "SeatPatch" });

/** DE-SEAT-OWNER: who holds a seat and which person answers for it. */
export const SeatOccupancy = z
  .object({
    mode: z
      .enum([
        "vacant",
        "person",
        "person_assisted",
        "de_assistant",
        "de_acting",
        "de_owner",
        "de_head",
      ])
      .describe("Derived from the holders, the DE's capacity and team leads (seatOccupancy)"),
    holder: z
      .object({
        membership_id: Uuid,
        kind: z.enum(["person", "digital_employee"]),
        display_name: z.string(),
        capacity: SeatAssignmentCapacity.nullable(),
        since: Timestamp,
      })
      .nullable()
      .describe("The person when one holds the seat, else its DE; null when vacant"),
    accountable: z
      .object({ seat_id: Uuid, membership_id: Uuid, display_name: z.string() })
      .nullable()
      .describe(
        "The person who answers for the seat: its own person, else the next person up the chain (where its approvals, escalations and late KPIs go)",
      ),
  })
  .meta({ id: "SeatOccupancy" });

export const SeatDetail = Seat.extend({
  assignments: z.array(SeatAssignment).describe("Active assignments"),
  occupancy: SeatOccupancy.optional().describe("DE-SEAT-OWNER: present with the flag on"),
}).meta({ id: "SeatDetail" });

export const SeatAssign = z
  .strictObject({ membership_id: Uuid, share: DecimalInput.optional() })
  .meta({ id: "SeatAssign" });
export const SeatUnassign = z.strictObject({ membership_id: Uuid }).meta({ id: "SeatUnassign" });

export const ScorecardQuery = z
  .object({
    period_type: PeriodType.optional().describe("Default week"),
    period: PeriodKey,
  })
  .superRefine(periodPairRule("period", "period_type", "week"));

export const SeatCostQuery = z
  .object({
    period_type: z.enum(["week", "month"]).optional().describe("Default month"),
    period: PeriodKey,
  })
  .superRefine(periodPairRule("period", "period_type", "month"));

export const CostRollup = z
  .object({
    by_kind: z.record(z.string(), z.int()),
    total_minor: z.int(),
    currency: Currency,
  })
  .meta({ id: "CostRollup" });

export const SeatCost = z
  .object({ ledger: z.array(CostEvent), rollup: CostRollup })
  .meta({ id: "SeatCost" });

export const SeatMetricsPut = z
  .strictObject({
    items: z
      .array(
        z.strictObject({
          metric_def_id: Uuid,
          kpi_weight: DecimalInput,
          goal_weight: DecimalInput,
          tracking_mode: TrackingMode,
          penalty_only: z.boolean(),
          position: z.int().min(0),
        }),
      )
      .max(LIMITS.list),
  })
  .meta({ id: "SeatMetricsPut" });

/** One line of a seat's metric set with its metric's name and key, so an editor can label it. */
export const SeatMetricLine = SeatMetric.extend({
  metric_key: z.string(),
  metric_name: z.string(),
}).meta({ id: "SeatMetricLine" });
export type SeatMetricLine = z.infer<typeof SeatMetricLine>;

/**
 * A seat's whole metric set as its reader sees it (every tracking mode, `off` and `penalty_only`
 * included) and the set's version, also sent as `ETag`. The version is derived from the set's
 * content (`seatMetricSetVersion`), so `PUT` under `If-Match` refuses a set someone changed since.
 */
export const SeatMetricSet = z
  .object({ data: z.array(SeatMetricLine), version: z.int().min(0) })
  .meta({ id: "SeatMetricSet" });
export type SeatMetricSet = z.infer<typeof SeatMetricSet>;

export { seatMetricSetVersion } from "../../seat-metric-set";

/**
 * KPI-ASSIGN: one measurable of a seat, written on its own (the KPI assignment editor), so
 * editing one KPI never rewrites the rest of the set. `position` absent keeps the row's place,
 * or appends a new row at the end.
 */
export const SeatMetricPut = z
  .strictObject({
    kpi_weight: DecimalInput,
    goal_weight: DecimalInput,
    tracking_mode: TrackingMode,
    penalty_only: z.boolean(),
    position: z.int().min(0).optional(),
  })
  .meta({ id: "SeatMetricPut" });

export const SeatMetricParams = z.object({ co: Uuid, id: Uuid, metric_id: Uuid });

// Digital employees -----------------------------------------------------------------------------

/**
 * WF-POLISH: what a DE or an agent is, as typed (the Custom add flow, the AG dialog) or synced
 * from its operating profile. Blank text is stored as null (the server's `profileText`).
 */
const ProfileText = (max: number) => z.string().trim().max(max).nullable();
/**
 * CLOCKWORK B2: role, goal and backstory are required of every DE and AG. A create that leaves one
 * out gets the platform default (`withProfileDefaults`); a field that is sent (create or patch)
 * must hold text, never blank or null, so no record loses one. The guided flows (the Add forms and
 * the MCP create tools) also hold a backstory to `BACKSTORY_MIN_CHARS`.
 */
const RequiredText = (max: number) => z.string().trim().min(1).max(max);
export const WorkerProfileFields = {
  role: RequiredText(2000).optional(),
  goal: RequiredText(4000).optional(),
  backstory: RequiredText(8000).optional(),
  standing_orders: ProfileText(20000).optional(),
};

export const DigitalEmployeeCreate = z
  .strictObject({
    display_name: NonEmpty,
    template_key: NonEmpty,
    seat_id: Uuid,
    ...WorkerProfileFields,
    runtime_ref: RuntimeRef.optional(),
    started_on: IsoDate.optional(),
    /**
     * DE-WIZARD: false leaves the seat's KPIs to the job (`PUT …/job` `kpis`), so the wizard's
     * KPI step is what the seat gets; omitted or true, the template's metric set is seeded.
     */
    seed_template_metrics: z.boolean().optional(),
    /**
     * AGENT-SPEC: the DE's first agent spec (the parts the Add-DE flow sets; the rest stays
     * unmanaged), written as version 1 and signed. Omitted: no spec until the DE editor sets one.
     */
    agent_spec: AgentSpecChanges.optional(),
  })
  .meta({ id: "DigitalEmployeeCreate" });

/** Status changes emit `digital_employee.{activated,paused,resumed,retired}`. */
export const DigitalEmployeePatch = z
  .strictObject({
    display_name: NonEmpty.optional(),
    /** HELPERS: the optional person name before the role (null or "" clears it). */
    person_name: z.string().trim().max(80).nullable().optional(),
    template_key: NonEmpty.optional(),
    runtime_ref: RuntimeRef.nullable().optional(),
    started_on: IsoDate.nullable().optional(),
    status: DigitalEmployeeStatus.exclude(["onboarding"]).optional(),
    /**
     * Phase 2 increment 6: the explicit choice of execution. `hermes` copies the confirmed panel
     * binding into `runtime_ref` (refused while a manual ref is set, or with no binding); `local`
     * clears it and pins the DE local. Runs on Hermes still need routing evidence (a probe).
     * Not together with `runtime_ref`.
     */
    execution: z.enum(["local", "hermes"]).optional(),
    /**
     * DISPATCHER-CORE: how many runs the DE may have live at once, each on its own subject (one
     * run per subject always holds, so parallel runs never duplicate work). 1..32 (managers).
     */
    max_concurrency: z.int().min(1).max(32).optional(),
    /** WF-POLISH: role, goal, backstory and the profile's standing orders (managers). */
    ...WorkerProfileFields,
  })
  .meta({ id: "DigitalEmployeePatch" });

export const ScorecardSummary = z
  .object({
    period_type: PeriodType,
    period_key: PeriodKey,
    kpi_score: z.number().nullable(),
    completeness: z.number(),
    band: Band,
  })
  .meta({ id: "ScorecardSummary" });

/** AGENT-MENTION: the roster leaves removed DEs out unless asked. */
export const DigitalEmployeeListQuery = PageQuery.extend({
  include_removed: z.enum(["true", "false"]).optional(),
});

/**
 * AGENT-MENTION: remove a DE (05 §DE configuration, a manager's offboarding): retire it if it is
 * not yet, revoke every live key, queue its LiteLLM keys for deletion, and hide it from the
 * roster. `confirm` is the DE's display name, typed by the person (the confirmation).
 */
export const DigitalEmployeeRemove = z
  .strictObject({ confirm: z.string().min(1).max(200) })
  .meta({ id: "DigitalEmployeeRemove" });

/**
 * PURGE-RETIRED-AGENTS: the admin types the agent's name; `delete_memory` deletes its Recall
 * memory instead of archiving it.
 */
export const AgentPurge = z
  .strictObject({
    confirm: z.string().min(1).max(200),
    delete_memory: z.boolean().optional(),
    /** With `delete_memory`: the plan's `memory.recall.delete.confirm_token`. */
    memory_confirm_token: z.string().min(1).max(500).optional(),
  })
  .meta({ id: "AgentPurge" });

/** PURGE-RETIRED-AGENTS: a count per kind of row (`removes`, `keeps`). */
const PurgeCounts = z.record(z.string(), z.int().min(0));

/**
 * PURGE-RETIRED-AGENTS: what a permanent removal does, read before anything happens. `blocked`:
 * why it cannot run now (`not_retired`: retire it first; `model_keys_pending`: its LiteLLM keys
 * are still being deleted). `removes`: rows that go. `keeps`: rows that stay, shown as
 * `removed_label`. `tasks`: its open tasks and the seat they move to (its seat's person, else up
 * the reporting chain to the first seat a person holds). `memory`: its Recall agent and the
 * spaces only it used (archived, or deleted when asked). `data`: the agent's own data, for the
 * "Download its data first" button.
 */
export const AgentPurgePlan = z
  .object({
    agent_kind: z.enum(["digital_employee", "workforce_agent"]),
    id: Uuid,
    name: z.string(),
    status: z.string(),
    removed_label: z.string(),
    blocked: z
      .object({ reason: z.enum(["not_retired", "model_keys_pending"]), detail: z.string() })
      .nullable(),
    removes: PurgeCounts,
    keeps: PurgeCounts,
    tasks: z.object({
      open: z.int().min(0),
      move: z.int().min(0),
      to: z
        .object({ seat_id: Uuid, seat_title: z.string(), holder_name: z.string().nullable() })
        .nullable(),
    }),
    memory: z.object({
      recall_agent: z.boolean(),
      spaces: z.array(z.object({ external_id: z.string(), name: z.string() })),
      writes: z.int().min(0),
      /**
       * Recall's previews: `archive` (its writes in shared spaces, always archived) and `delete`
       * (what "Also delete its memory" removes, with the token the purge sends back). `state`:
       * `none` (no Recall agent), `off` (Recall not connected), `not_retired` (its Recall agent is
       * still being retired), `held` (a legal hold refuses the delete), `ready`, `error`.
       */
      recall: z.object({
        state: z.enum(["none", "off", "not_retired", "held", "ready", "error"]),
        archive: PurgeCounts.nullable(),
        delete: z.object({ counts: PurgeCounts, confirm_token: z.string() }).nullable(),
      }),
    }),
    data: z.record(z.string(), z.unknown()),
  })
  .meta({ id: "AgentPurgePlan" });

/** PURGE-RETIRED-AGENTS: what was removed and kept (the counts the audit event carries). */
export const AgentPurgeResult = z
  .object({
    agent_kind: z.enum(["digital_employee", "workforce_agent"]),
    id: Uuid,
    removed_label: z.string(),
    removed: PurgeCounts,
    kept: PurgeCounts,
    tasks_reassigned: z.int().min(0),
    memory: z.object({ mode: z.enum(["archive", "delete"]), spaces: z.int().min(0) }),
  })
  .meta({ id: "AgentPurgeResult" });

/**
 * AGENT-MENTION (06 §Main screens, the DE profile's job configuration): the DE's standing orders
 * as saved and as deployed. `saved` is the company's override (null: the template's own),
 * `template_text` the deployed version's own text, `next` what the next deploy carries, and
 * `deployed` the text inside the bundle the DE runs now (null text: the bundle is in object
 * storage, only its hash is known). `pending`: the next deploy changes the standing orders.
 */
export const DeStandingOrders = z
  .object({
    template_id: Uuid.nullable(),
    template_name: z.string().nullable(),
    template_version_id: Uuid.nullable(),
    template_text: z.string().nullable(),
    saved: z
      .object({
        text: z.string().nullable(),
        version: z.int().min(1),
        updated_at: Timestamp,
      })
      .nullable(),
    next: z.string().nullable(),
    deployed: z
      .object({
        text: z.string().nullable(),
        deploy_id: Uuid,
        bundle_sha: z.string(),
        deployed_at: Timestamp.nullable(),
      })
      .nullable(),
    pending: z.boolean(),
    /** The caller may change the override (admin, or the seat's supervisor). */
    can_edit: z.boolean(),
  })
  .meta({ id: "DeStandingOrders" });

/**
 * CHART-HOWIWORK: who saved a "How I work" version (a person's name; null for the system or a
 * membership the reader cannot resolve).
 */
export const DeHowIWorkAuthor = z
  .object({
    kind: z.string().nullable(),
    membership_id: Uuid.nullable(),
    name: z.string().nullable(),
  })
  .meta({ id: "DeHowIWorkAuthor" });

/**
 * CHART-HOWIWORK: a DE's "How I work" — its process in plain words (steps, when it asks a person,
 * which agents it uses). The DE receives it at every run start, on top of its container files
 * (the base instructions). `version` 0 and null text: none written yet. Read by anyone who sees
 * the DE; `can_edit`: a company admin or owner, or the DE's supervisor.
 */
export const DeHowIWork = z
  .object({
    text: z.string().nullable(),
    version: z.int().min(0),
    updated_at: Timestamp.nullable(),
    updated_by: DeHowIWorkAuthor.nullable(),
    can_edit: z.boolean(),
  })
  .meta({ id: "DeHowIWork" });

/**
 * CHART-HOWIWORK: save a new version. `base_version` is the version the editor started from: a
 * different current version is 409 `conflict` (someone saved in between); omitted, the save
 * goes on top of whatever is current. Blank text clears it.
 */
export const DeHowIWorkPut = z
  .strictObject({
    text: z.string().trim().max(20000).nullable(),
    base_version: z.int().min(0).optional(),
  })
  .meta({ id: "DeHowIWorkPut" });

/** CHART-HOWIWORK: one saved version, newest first in the history. */
export const DeHowIWorkVersion = z
  .object({
    version: z.int().min(1),
    text: z.string().nullable(),
    created_at: Timestamp,
    author: DeHowIWorkAuthor,
  })
  .meta({ id: "DeHowIWorkVersion" });

export const DeHowIWorkVersionsQuery = z.object({
  /** Versions older than this one (the previous page's `next_cursor`). */
  cursor: z
    .string()
    .max(9)
    .regex(/^[1-9][0-9]{0,8}$/)
    .optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

/**
 * DE-WIZARD (review #12): where a DE is on its way to work, one word for the roster and the
 * profile. `draft`: its set-up was not submitted and nothing is staged; `awaiting_approval`:
 * done-for-you, waiting for HTVA to stage it or for the owner to approve the first deploy;
 * `deploying`: a deploy is staged (no approval needed), approved or handed off; `live`: active;
 * `not_live`: paused, retired, a failed or rejected deploy, or deployed but still in setup (its
 * model key not delivered) — `reason` says which, in a sentence.
 */
export const DeReadinessState = z.enum([
  "draft",
  "awaiting_approval",
  "deploying",
  "live",
  "not_live",
]);
export const DeReadiness = z
  .object({ state: DeReadinessState, reason: z.string().nullable() })
  .meta({ id: "DeReadiness" });

export const DigitalEmployeeSummary = DigitalEmployee.extend({
  display_name: z.string(),
  seat: z.object({ id: Uuid, title: z.string() }).nullable(),
  readiness: DeReadiness,
  /**
   * WORKFORCE (2026-10-26): whether the caller may pause, resume or rename this DE — a manager,
   * or a person holding, supervising or standing above its seat at any depth.
   */
  can_intervene: z.boolean().optional(),
}).meta({ id: "DigitalEmployeeSummary" });

/**
 * DE-WIZARD: the DE's job set-up as the wizard's steps and the profile's edit mode read it. The
 * `de_job` row's fields (`version` 0 and the defaults when the DE has none yet), the seat's
 * assigned KPIs (every `seat_metric` of its seat, read from the seat itself, so a save never
 * drops one it did not show) with the current weekly target and whether the DE reports it, and
 * its post approval (the seat's settings: `require_approval` holds `message`; the approver seat,
 * null = the seat's supervisor).
 */
export const DeJobKpi = z
  .object({
    metric_def_id: Uuid,
    key: z.string(),
    name: z.string(),
    unit: MetricUnit,
    tracking_mode: TrackingMode,
    weekly_target: Decimal.nullable(),
    reported_by_agent: z.boolean(),
    duty: DeReportingDuty.nullable(),
  })
  .meta({ id: "DeJobKpi" });
export const DeJobView = z
  .object({
    de_membership_id: Uuid,
    seat_id: Uuid.nullable(),
    path: DeployPath,
    submitted_at: Timestamp.nullable(),
    home_channel_id: Uuid.nullable(),
    reporting: z.array(DeJobReporting),
    kpis: z.array(DeJobKpi),
    post_approval: z.object({ required: z.boolean(), approver_seat_id: Uuid.nullable() }),
    version: z.int().min(0),
    /** The caller may save it (managers). */
    can_edit: z.boolean(),
  })
  .meta({ id: "DeJobView" });

/**
 * DE-WIZARD: save part of the job (every field optional; only what is sent changes). `kpis`
 * adds or updates those of the seat's KPIs (a new one is scored at weight 1; a target writes a
 * weekly target rule from today) and `remove_metric_def_ids` removes KPIs explicitly — a KPI
 * left out of `kpis` is never dropped. `submit` marks the set-up submitted (Draft ends).
 */
export const DeJobPut = z
  .strictObject({
    path: DeployPath.optional(),
    submit: z.literal(true).optional(),
    home_channel_id: Uuid.nullable().optional(),
    kpis: z
      .array(
        z.strictObject({
          metric_def_id: Uuid,
          weekly_target: DecimalInput.nullable().optional(),
          reported_by_agent: z.boolean(),
          duty: DeReportingDuty.optional(),
        }),
      )
      .max(LIMITS.list)
      .optional(),
    remove_metric_def_ids: z.array(Uuid).max(LIMITS.list).optional(),
    post_approval: z
      .strictObject({ required: z.boolean(), approver_seat_id: Uuid.nullable() })
      .optional(),
  })
  .meta({ id: "DeJobPut" });

/**
 * CONNECTIONS-AREA (Team › Connections › Agent keys): one company key that lets an agent into
 * CompanyOS, with the member it is bound to. Never the secret: the prefix only.
 */
export const CompanyAgentKey = AgentKey.extend({
  holder: z.object({
    membership_id: Uuid,
    kind: MembershipKind,
    display_name: z.string(),
  }),
}).meta({ id: "CompanyAgentKey" });

export const DigitalEmployeeProfile = DigitalEmployeeSummary.extend({
  supervisor: z
    .object({
      seat_id: Uuid,
      title: z.string(),
      membership_id: Uuid.nullable(),
      name: z.string().nullable(),
    })
    .nullable(),
  scorecard: ScorecardSummary.nullable().describe("Current week; null without the kpi feature"),
  keys: z.array(AgentKey),
  /**
   * DC-05: the verification override the DE's current deploy runs under (a pilot of a version
   * with no verification skill), shown with the unverified badge; null otherwise.
   */
  verification_override: z
    .object({
      deploy_id: Uuid,
      reason: z.string(),
      pilot_until: Timestamp,
      /** `pilot_until` has passed: the pilot is over and the DE stays unverified. */
      expired: z.boolean(),
    })
    .nullable(),
  /**
   * AGENT-POLISH: the DE's AiGentPanel profile: allocated at its first staging and never
   * recomputed (Phase 2: `cos-<company>-<role>-<name>-<id>`), `deployed` once handed off, or the
   * name the first staging will allocate. Null for a DE with no seat.
   */
  aigentpanel: z
    .object({
      profile_name: z.string(),
      instance_id: z.string().nullable(),
      deployed: z.boolean(),
      /**
       * Phase 2: why the stored name cannot be handed off ("profile name not accepted by the
       * panel; rename"); null when it can.
       */
      problem: z.string().nullable().optional(),
      /**
       * Phase 2: the company's one Hermes container as this DE sees it: whether this DE owns it,
       * its key state, and what to say ("container key unconfirmed" until a probe passed).
       * `not_imported`: a pre-registry container no operator has imported yet.
       */
      container: z
        .object({
          owner: z.boolean(),
          key_state: z.enum(["proposed", "live", "retired", "not_imported"]),
          message: z.string().nullable(),
        })
        .nullable()
        .optional(),
      /**
       * Phase 2 increment 6: how the DE executes. `mode` is `hermes` only after the explicit
       * opt-in (or a manual `runtime_ref`); `bound` once a deploy confirmed the binding;
       * `routable` only while routing evidence (a passed probe) holds. `message` says it ("deployed
       * on AiGentPanel: runnable on Hermes (off)", "probe required"); `notice` is what opting in
       * means today (F7).
       */
      execution: z
        .object({
          mode: z.enum(["local", "hermes"]),
          source: RuntimeRefSource.nullable(),
          bound: z.boolean(),
          routable: z.boolean(),
          verified_at: Timestamp.nullable(),
          /**
           * The routing evidence: `verified` while a passed probe's evidence holds for this DE,
           * `invalidated` when a probe passed once but its evidence is gone, else `unverified`.
           */
          evidence: z.enum(["unverified", "verified", "invalidated"]).optional(),
          message: z.string().nullable(),
          notice: z.string(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  /**
   * WORKFORCE (2026-10-26): a self-hosted DE (its lead profile's `overrides.self_hosted`) runs
   * outside AiGentPanel — a Hermes profile in a ClawPanel container, recorded in the lead
   * profile's `overrides.runtime` (null when ops has not recorded where yet). Null for a DE that
   * is not self-hosted.
   */
  self_hosted: z.object({ runtime: WorkerRuntime.nullable() }).nullable().optional(),
  /**
   * WF-FIX (2026-10-26): when the DE last did something. `last_run_at` is its newest run's
   * `created_at`; `last_mcp_at` the newest `last_used_at` across its membership's agent keys (an
   * MCP call records it, at most once a minute per key). Null when it never has.
   */
  activity: z
    .object({ last_run_at: Timestamp.nullable(), last_mcp_at: Timestamp.nullable() })
    .optional(),
  /** CHART-HOWIWORK: its "How I work" (the current version) and whether the caller may edit it. */
  how_i_work: DeHowIWork.optional(),
}).meta({ id: "DigitalEmployeeProfile" });

export const AgentKeyCreate = z
  .strictObject({
    scopes: z.array(IngestKeyScope).min(1).max(IngestKeyScope.options.length),
    expires_at: Timestamp.nullable().optional(),
    /**
     * Ingest rule 7: the key may post items older than 90 days (first-time history imports).
     * Only an agency operator may issue one; anyone else gets 403.
     */
    backfill: z.boolean().optional(),
    /**
     * Meter namespaces (`{system}:{scope}`) for `cost` items beyond the DE's own
     * `litellm:{alias}`; unique to one DE in the company.
     */
    meter_namespaces: z.array(MeterNamespace).max(16).optional(),
  })
  .meta({ id: "AgentKeyCreate" });

/** The plaintext (`cosk_in_…`) is returned once, to the creator only, and never stored. */
export const AgentKeyCreated = z
  .object({
    key: z.object({
      id: Uuid,
      prefix: z.string().length(12),
      scopes: z.array(IngestKeyScope),
      backfill: z.boolean(),
      meter_namespaces: z.array(z.string()),
    }),
    plaintext: z.string().startsWith("cosk_in_"),
  })
  .meta({ id: "AgentKeyCreated" });

export const TimelineQuery = z.object({
  after: Timestamp.optional().describe(
    "Only events after this instant (13; a filter, not the page cursor)",
  ),
  cursor: z
    .string()
    .max(512)
    .optional()
    .describe("Opaque, company-bound `next_cursor` of the previous page (occurred_at, event_id)"),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

/** Activity and outcome events of a DE, client-visible payload fields only. */
export const TimelineItem = z
  .object({
    event_id: Uuid,
    kind: z.enum(["activity", "outcome"]),
    type: DottedName,
    occurred_at: Timestamp,
    quantity: z.number(),
    value: z.number().optional(),
    subject: ExternalSubject.optional(),
    attrs: IngestAttrs.optional(),
  })
  .meta({ id: "TimelineItem" });

export const LitellmKeyCreate = z
  .strictObject({
    max_budget_minor: z.int().positive(),
    currency: Currency.optional(),
    models: z.array(z.string().max(128)).max(50).optional(),
  })
  .meta({ id: "LitellmKeyCreate" });

/**
 * A provisioning attempt as the operator sees it. `issued`: the key waits, sealed for its
 * recipient, until `expires_at` (`GET …/litellm-key`, then `POST …/litellm-key/ack`);
 * `reserved`: the attempt is still running (or will be retried by the reconciler); `failed` /
 * `expired`: `error` says why and a new attempt may be started.
 */
export const LitellmProvisioningView = z
  .object({
    attempt: z.int().min(0),
    state: LitellmProvisioningState,
    key_alias: z.string(),
    expires_at: Timestamp.nullable(),
    error: z.string().nullable(),
    /** Remote keys whose compensating delete is still pending (retried, never dropped). */
    pending_deletes: z.int().min(0),
  })
  .meta({ id: "LitellmProvisioningView" });

export const LitellmKeyResult = z
  .object({ digital_employee: DigitalEmployee, provisioning: LitellmProvisioningView })
  .meta({ id: "LitellmKeyResult" });

/** The issued key, for its recipient only, until acknowledged or expired. */
export const LitellmKeyIssuance = z
  .object({
    attempt: z.int().min(1),
    key_alias: z.string(),
    virtual_key: z.string(),
    expires_at: Timestamp,
  })
  .meta({ id: "LitellmKeyIssuance" });

export const LitellmKeyAck = z
  .strictObject({ attempt: z.int().min(1) })
  .meta({ id: "LitellmKeyAck" });

// Teams, org chart, responsibilities, recurring work, templates, webhooks (M1-02) -------------

export const TeamCreate = z
  .strictObject({
    name: NonEmpty,
    parent_team_id: Uuid.nullable().optional(),
    lead_seat_id: Uuid.nullable().optional(),
    human_owner_seat_id: Uuid.nullable().optional(),
  })
  .meta({ id: "TeamCreate" });
/** `composition` is derived from the seats' assignments and never written. */
export const TeamPatch = TeamCreate.partial().meta({ id: "TeamPatch" });

export const TeamDetail = Team.extend({ members: z.array(TeamMember) }).meta({ id: "TeamDetail" });

export const TeamMemberAdd = z.strictObject({ seat_id: Uuid }).meta({ id: "TeamMemberAdd" });

/** A seat in the org chart: names and titles only, never metrics (05 `org_chart#viewer`). */
export const OrgChartSeat = z
  .object({
    id: Uuid,
    role_id: Uuid,
    title: z.string(),
    status: SeatStatus,
    supervisor_seat_id: Uuid.nullable(),
    /** CHART-STAFF: drawn at the side of its supervisor's card, not in the row of reports. */
    staff: z.boolean(),
    /**
     * CHART-TABLE-ORDER: the place among the seats with the same supervisor (left to right, top
     * to bottom); null: never ordered — after the ordered ones, titles A–Z.
     */
    sort_order: z.int().min(0).nullable(),
    holders: z.array(
      z.object({
        membership_id: Uuid,
        membership_kind: MembershipKind,
        display_name: z.string(),
        share: Decimal,
        capacity: SeatAssignmentCapacity.nullable()
          .optional()
          .describe(
            "DE-SEAT-OWNER: a DE's assistant · acting · owner (null or absent: a person, or before the flag)",
          ),
      }),
    ),
  })
  .meta({ id: "OrgChartSeat" });

/**
 * CHART-AGENTS: an agent (AG) on the chart, under the holder it reports to (a DE or a person).
 * Only agents whose reports-to is a holder the chart shows; retired agents never.
 */
export const OrgChartAgent = z
  .object({
    id: Uuid,
    slug: z.string(),
    name: z.string(),
    status: WorkforceAgentStatus.exclude(["retired"]),
    reports_to_membership_id: Uuid,
    reports_to_kind: WorkforceAgentReportsToKind,
    /**
     * HELPERS-2: the other shown DE holders that use it; it is listed under them too. An agent
     * whose home is not shown is returned when one of these is.
     */
    used_by_membership_ids: z.array(Uuid),
  })
  .meta({ id: "OrgChartAgent" });

/** `GET org-chart`: seats, edges and teams in one call (04 §Org); `proposed` seats are hidden. */
export const OrgChart = z
  .object({
    seats: z.array(OrgChartSeat),
    /** CHART-AGENTS: grouped by `reports_to_membership_id` (then name). */
    agents: z.array(OrgChartAgent),
    edges: z.array(SeatEdge),
    teams: z.array(Team),
    team_members: z.array(TeamMember),
    /**
     * SHARED-DES: the serves links of shared seats (seat → team, seat or company, with grants).
     * The chart draws a linked card under each served lead that is not an ancestor of the home
     * seat; links never count in a roll-up. Absent from an older API: no links.
     */
    service_links: z
      .array(
        z.object({
          id: Uuid,
          seat_id: Uuid,
          target_kind: z.enum(["seat", "team", "company"]),
          target_id: Uuid.nullable(),
          grants: z.object({ talk: z.boolean(), task: z.boolean(), see: z.boolean() }),
        }),
      )
      .optional(),
  })
  .meta({ id: "OrgChart" });

/**
 * `GET org-chart/dimensions` (CHART-DIMENSIONS): what each seat card shows beyond its holder and
 * title, cut to the caller. `responsibilities` is the floor: every chart viewer gets it on every
 * seat. A gated key (`kpi`, `load`, `cost`) is absent when the caller's relation to that seat
 * does not reach the company's audience for it (`ChartVisibilityLevel`; company managers reach
 * everything); present and `null` when the caller may see it but there is nothing (no metrics,
 * no cost). `dimensions` lists what the company serves at all (`kpi` only with the `kpi`
 * add-on); `visibility` is the company's audience per gated dimension, so the chart can say who
 * sees what.
 */
export const OrgChartSeatDimensions = z
  .object({
    seat_id: Uuid,
    responsibilities: z
      .object({
        count: z.int().min(0),
        /** CHART-LENSES: of those, the ones with measurables (`kpi_metric_ids`), the coverage. */
        measurable: z.int().min(0),
        /** The heaviest five, by weight then name; `measured` says whether each has metrics. */
        top: z
          .array(z.object({ id: Uuid, name: z.string(), weight: Decimal, measured: z.boolean() }))
          .max(5),
      })
      .optional(),
    kpi: z
      .object({
        band: Band,
        kpi_score: z.number().nullable(),
        goal_score: z.number().nullable(),
        completeness: z.number(),
        /** Metrics on the seat's scorecard the caller may see. */
        metrics: z.int().min(0),
        /**
         * CHART-KB: the first four of them by KPI weight — name, this week's actual, target and
         * attainment — the measurable rows of the Performance and Execution cards.
         */
        top: z
          .array(
            z.object({
              metric_def_id: Uuid,
              name: z.string(),
              unit: MetricUnit,
              actual: z.number().nullable(),
              target: z.number().nullable(),
              attainment: z.number().nullable(),
              state: ScoreState,
              /** TEAM-KPI: the metric's own bands (null: the company's) and the row's health. */
              bands: MetricBands.nullable().optional(),
              health: MetricHealth.nullable().optional(),
            }),
          )
          .max(4),
      })
      .nullable()
      .optional(),
    load: z
      .object({
        /** Open tasks (not completed) assigned to the seat. */
        open: z.int().min(0),
        /** Of those, past their due date. */
        overdue: z.int().min(0),
        /** The sum of their estimates (tasks without one count nothing). */
        estimate_minutes: z.int().min(0),
      })
      .optional(),
    cost: z
      .object({
        actual_minor: z.int(),
        budget_minor: z.int().nullable(),
        utilization: Decimal.nullable(),
        currency: Currency,
        restricted_hidden: z.boolean(),
        /**
         * DE-BUDGET-PACE: the seat's DE against its own limit this period (its effective budget:
         * the hard cap, else the amount) and its pace; absent on a seat no DE holds.
         */
        de_budget: z
          .object({
            membership_id: Uuid,
            status: z.enum(["on_track", "ahead_of_pace", "reserve_reached", "no_budget"]),
            spent_minor: z.int(),
            limit_minor: z.int().nullable(),
            projected_minor: z.int().nullable(),
            runout_at: Timestamp.nullable(),
          })
          .optional(),
      })
      .nullable()
      .optional(),
    /**
     * CHART-LENSES: the Overall lens, the weighted combination of the scored lenses computed on
     * the server from everything the seat has (under the caller's finance access), present when
     * the caller reaches `visibility.overall`. `scored` of `total` says how many weighted lenses
     * had data; `score` is null when none did.
     */
    overall: z
      .object({
        score: z.number().nullable(),
        band: ChartLensBand,
        scored: z.int().min(0),
        total: z.int().min(0),
      })
      .optional(),
    /**
     * CHART-KB: the seat's priorities of the current quarter (`goal.kind = priority`), the
     * Keystones lens; present when the caller reaches `visibility.keystones`. `top` is the first
     * three with their milestones' progress.
     */
    keystones: z
      .object({
        quarter: PeriodKey,
        count: z.int().min(0),
        on: z.int().min(0),
        at_risk: z.int().min(0),
        off: z.int().min(0),
        top: z
          .array(
            z.object({
              id: Uuid,
              title: z.string(),
              track: GoalTrack,
              milestones_done: z.int().min(0),
              milestones_total: z.int().min(0),
            }),
          )
          .max(3),
      })
      .optional(),
    /**
     * CHART-KB: how much of the seat's required setup is in place — `met` of `required` items,
     * `score` the share in %, `missing` the required items not yet there that the caller may
     * see, `hidden` the missing ones in a dimension outside the caller's audience (counted in
     * the score, not named). Present for the seat's holders, its reporting line and managers.
     */
    completeness: z
      .object({
        score: z.int().min(0).max(100),
        met: z.int().min(0),
        required: z.int().min(0),
        missing: z.array(ChartCompletenessItem),
        hidden: z.int().min(0),
      })
      .optional(),
    /**
     * KPI-VISIBILITY (05 §KPI visibility): whether the caller may read this seat's KPI data
     * (the `kpi` group: its own seats, the branch below, and every grant naming it).
     */
    can_view_kpis: z.boolean(),
  })
  .meta({ id: "OrgChartSeatDimensions" });

const LensTwoSteps = z.object({ green: z.number(), amber: z.number() });

/** CHART-LENSES: the company's effective lens configuration (its settings over the defaults). */
export const OrgChartLensConfig = z
  .object({
    thresholds: z.object({
      responsibilities: LensTwoSteps,
      execution: LensTwoSteps,
      tasks: LensTwoSteps,
      finance: z.object({ near: z.number(), over: z.number() }),
      keystones: LensTwoSteps,
      overall: LensTwoSteps,
    }),
    weights: z.object({
      responsibilities: z.number(),
      performance: z.number(),
      execution: z.number(),
      tasks: z.number(),
      finance: z.number(),
      keystones: z.number(),
    }),
    /** The lens a viewer opens on before choosing one. */
    default_lens: ChartLens,
  })
  .meta({ id: "OrgChartLensConfig" });

/** CHART-KB: the company's completeness rules in force, and the summary over the seats the caller reaches. */
export const OrgChartCompleteness = z
  .object({
    /** The quarter the keystone item is counted for. */
    quarter: PeriodKey,
    required: z.record(ChartCompletenessItem, z.boolean()),
    de_exempt_finance: z.boolean(),
    /** Over the seats whose completeness the caller may see: how many, how many complete, the mean %. */
    summary: z.object({
      seats: z.int().min(0),
      complete: z.int().min(0),
      mean: z.int().min(0).max(100),
    }),
  })
  .meta({ id: "OrgChartCompleteness" });

export const OrgChartDimensions = z
  .object({
    week: PeriodKey,
    month: PeriodKey,
    dimensions: z.array(ChartDimension),
    /** The company's default choice (`settings.chart.dimensions`, else teams and responsibilities). */
    default_dimensions: z.array(ChartDimension),
    visibility: z.record(ChartGatedDimension, ChartVisibilityLevel),
    lenses: OrgChartLensConfig,
    completeness: OrgChartCompleteness,
    /**
     * KPI-VISIBILITY: the seats whose KPI data the caller may read (the `kpi` group), the one read
     * the KPIs tree and the chart widen by; every seat for a company manager.
     */
    kpi_visible_seat_ids: z.array(Uuid),
    seats: z.array(OrgChartSeatDimensions),
  })
  .meta({ id: "OrgChartDimensions" });

/** KPIS-MODE: the seats whose KPI data (scorecard band, values, grid rows) the caller may see. */
export const OrgChartKpiVisibility = z
  .object({ kpi_visible_seat_ids: z.array(Uuid) })
  .meta({ id: "OrgChartKpiVisibility" });

/**
 * KPI-VISIBILITY (05 §KPI visibility, founder-approved 2026-10-22): a grant that widens whose KPI
 * data a principal reads. `groups` empty: every dimension group ("all KPI dimensions").
 * `principal_name` is the role's, seat's, team's or person's name as Setup lists it (null when
 * the principal is gone).
 */
export const KpiGrant = z
  .object({
    id: Uuid,
    principal_kind: KpiPrincipalKind,
    principal_id: Uuid,
    principal_name: z.string().nullable(),
    scopes: z.array(KpiScope).min(1),
    groups: z.array(KpiGroup),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "KpiGrant" });

export const KpiGrantList = z.object({ items: z.array(KpiGrant) }).meta({ id: "KpiGrantList" });

export const KpiGrantCreate = z
  .strictObject({
    principal_kind: KpiPrincipalKind,
    principal_id: Uuid,
    scopes: z.array(KpiScope).min(1).max(5),
    groups: z.array(KpiGroup).max(4).optional(),
  })
  .meta({ id: "KpiGrantCreate" });

export const KpiGrantPatch = z
  .strictObject({
    scopes: z.array(KpiScope).min(1).max(5).optional(),
    groups: z.array(KpiGroup).max(4).optional(),
  })
  .meta({ id: "KpiGrantPatch" });

export const KpiVisibilityPreviewQuery = z
  .object({ membership_id: Uuid.describe("The person to preview as") })
  .meta({ id: "KpiVisibilityPreviewQuery" });

/** `GET kpi-visibility/preview`: the seats a person would see KPI data on, per group. */
export const KpiVisibilityPreview = z
  .object({
    membership_id: Uuid,
    /** A company manager: everything, whatever the grants. */
    manager: z.boolean(),
    /** The seats the person holds (the anchors of the default and of their grants). */
    held_seat_ids: z.array(Uuid),
    groups: z.record(KpiGroup, z.object({ all: z.boolean(), seat_ids: z.array(Uuid) })),
    /** The grants that reach the person. */
    grant_ids: z.array(Uuid),
  })
  .meta({ id: "KpiVisibilityPreview" });

export const OrgChartDimensionsQuery = z
  .object({ period: PeriodKey.optional().describe("The week; default the current week") })
  .meta({ id: "OrgChartDimensionsQuery" });

export const SeatEdgeInput = z
  .strictObject({
    from_seat_id: Uuid,
    to_seat_id: Uuid,
    kind: SeatEdgeKind,
    policy: SeatEdgePolicy.optional(),
  })
  .meta({ id: "SeatEdgeInput" });

/**
 * Batch replace of the edges of `kinds` (default all three): edges absent from `edges` are
 * removed. Validated as a whole: acyclic per kind, at most one `reports_to` per seat, and the
 * supervisor rule (a DE seat reports to a seat held by a person). `reports_to` edges and
 * `seat.supervisor_seat_id` are kept equal.
 */
export const OrgChartEdgesPut = z
  .strictObject({
    kinds: z.array(SeatEdgeKind).min(1).max(3).optional(),
    edges: z.array(SeatEdgeInput).max(2000),
  })
  .meta({ id: "OrgChartEdgesPut" });

/**
 * CHART-TABLE-ORDER: `PUT org-chart/order` — the seats reporting to `supervisor_seat_id` (null:
 * the top of the chart) in their new order; each gets its index. Every listed seat must already
 * report there (a move to another supervisor is `PUT edges`, with its rules); seats left out keep
 * their place value.
 */
export const OrgChartOrderPut = z
  .strictObject({
    supervisor_seat_id: Uuid.nullable(),
    seat_ids: z.array(Uuid).min(1).max(500),
  })
  .meta({ id: "OrgChartOrderPut" });

export const OrgChartProblem = z
  .object({
    code: z.enum([
      "cycle",
      "duplicate_reports_to",
      "supervisor_required",
      "unknown_seat",
      "seat_closed",
    ]),
    seat_ids: z.array(Uuid),
    message: z.string(),
  })
  .meta({ id: "OrgChartProblem" });

/** `POST org-chart/simulate`: what `PUT edges` with the same body would change; writes nothing. */
export const OrgChartSimulation = z
  .object({
    valid: z.boolean(),
    problems: z.array(OrgChartProblem),
    added: z.array(SeatEdgeInput),
    updated: z.array(SeatEdge),
    removed: z.array(SeatEdge),
    supervisor_changes: z.array(
      z.object({ seat_id: Uuid, from: Uuid.nullable(), to: Uuid.nullable() }),
    ),
  })
  .meta({ id: "OrgChartSimulation" });

export const ResponsibilityCreate = z
  .strictObject({
    name: NonEmpty,
    description: FreeText.nullable().optional(),
    weight: DecimalInput.optional(),
    kpi_metric_ids: z.array(Uuid).max(LIMITS.list).optional(),
  })
  .meta({ id: "ResponsibilityCreate" });
export const ResponsibilityPatch = ResponsibilityCreate.extend({
  status: ResponsibilityStatus.optional(),
})
  .partial()
  .meta({ id: "ResponsibilityPatch" });

/** CHART-KB: a milestone as written (`id` keeps an existing one; `done` sets or clears `done_at`). */
export const GoalMilestoneInput = z
  .strictObject({
    id: Uuid.optional(),
    title: NonEmpty,
    due_at: Timestamp.nullable().optional(),
    done: z.boolean().optional(),
  })
  .meta({ id: "GoalMilestoneInput" });

/**
 * CHART-KB (04 `goals` CRUD; 06 §Quarterly priorities): a goal on a seat. The default kind is
 * `priority`, whose `period_key` is a quarter (`2026-Q4`; default the current one).
 */
export const GoalCreate = z
  .strictObject({
    kind: GoalKind.optional(),
    title: NonEmpty,
    period_key: PeriodKey.nullable().optional(),
    due_at: Timestamp.nullable().optional(),
    track: GoalTrack.optional(),
    metric_def_id: Uuid.nullable().optional(),
    target: DecimalInput.nullable().optional(),
    milestones: z.array(GoalMilestoneInput).max(LIMITS.list).optional(),
  })
  .meta({ id: "GoalCreate" });
/** `milestones`, when given, replaces the goal's set (rows absent from it are removed). */
export const GoalPatch = GoalCreate.omit({ kind: true })
  .extend({ state: GoalState.optional() })
  .partial()
  .meta({ id: "GoalPatch" });

/** A goal with its milestones in position order. */
export const GoalDetail = Goal.extend({ milestones: z.array(GoalMilestone) }).meta({
  id: "GoalDetail",
});

export const GoalListQuery = PageQuery.extend({
  kind: GoalKind.optional(),
  period_key: PeriodKey.optional(),
  state: GoalState.optional(),
}).meta({ id: "GoalListQuery" });

/**
 * PRIORITIES-HOME (06 §Cadence, screen "Priorities"): the company's priorities of one quarter,
 * default the current one. Every state is listed (an archived priority is `dropped`).
 */
export const PriorityListQuery = z
  .object({ quarter: PeriodKey.optional() })
  .meta({ id: "PriorityListQuery" });

/**
 * The Priorities page's read: the quarter's priorities (`goal.kind = priority`) with their
 * milestones, cut to the seats the caller's relation reaches under the company's Keystones
 * audience (`settings.chart.visibility.keystones`, the chart's rule; managers reach all).
 * `hidden_seats` counts the seats with priorities this quarter the caller may not see (a count,
 * never their titles); `writable_seat_ids` are the seats whose priorities the caller may write
 * (05 `goal` editor: the seat's holder, or a manager on every seat); `quarters` are the
 * quarters with any priority, with the current and the next, newest first.
 */
export const PriorityBoard = z
  .object({
    quarter: PeriodKey,
    current_quarter: PeriodKey,
    next_quarter: PeriodKey,
    quarters: z.array(PeriodKey),
    visibility: ChartVisibilityLevel,
    hidden_seats: z.int().min(0),
    writable_seat_ids: z.array(Uuid),
    data: z.array(GoalDetail),
  })
  .meta({ id: "PriorityBoard" });

/**
 * Quarter roll-over: every unfinished (`open`) priority of `from` on the seats the caller may
 * write (or only `seat_ids`) is copied into `to` with its milestones not yet done, and closed in
 * `from` as `missed`. A priority whose seat already has one of the same title in `to` is skipped,
 * so a second run creates nothing.
 */
export const PriorityCarryOver = z
  .strictObject({
    from: PeriodKey,
    to: PeriodKey,
    seat_ids: z.array(Uuid).max(LIMITS.list).optional(),
  })
  .meta({ id: "PriorityCarryOver" });

export const PriorityCarryOverResult = z
  .object({
    created: z.array(GoalDetail),
    closed: z.int().min(0),
    skipped: z.int().min(0),
  })
  .meta({ id: "PriorityCarryOverResult" });

export const RecurringWorkCreate = z
  .strictObject({
    /** RFC 5545 RRULE (`FREQ=WEEKLY;BYDAY=MO`), evaluated in the company's time zone. */
    schedule: z.string().min(5).max(500),
    board_id: Uuid.nullable().optional(),
    task_template: TaskTemplate,
  })
  .meta({ id: "RecurringWorkCreate" });
export const RecurringWorkPatch = RecurringWorkCreate.extend({
  status: RecurringWorkStatus.optional(),
})
  .partial()
  .meta({ id: "RecurringWorkPatch" });

export const TemplateCreate = z
  .strictObject({
    name: NonEmpty,
    description: FreeText.nullable().optional(),
    spec: TemplateSpec,
  })
  .meta({ id: "TemplateCreate" });
/** A new `spec` bumps `version`. */
export const TemplatePatch = TemplateCreate.partial().meta({ id: "TemplatePatch" });

export const TemplateApply = z
  .strictObject({
    params: boundedRecord(z.string().max(LIMITS.name), 20).optional(),
  })
  .meta({ id: "TemplateApply" });

/** One line of a template plan (`templates.preview`; `skipped` of `templates.apply`). */
export const TemplatePlanItem = z
  .object({
    kind: z.enum([
      "role",
      "team",
      "team_member",
      "seat",
      "responsibility",
      "recurring_work",
      "metric",
      "space",
      "channel",
      "board",
      // W1-FIXES (T1 review): a board's custom fields and views, so the plan shows them
      "field",
      "view",
      "task",
    ]),
    /** The spec key, where the entry has one. */
    key: z.string().nullable(),
    /** Name or title after `{{param}}` substitution. */
    name: z.string(),
    /** `reuse`: an existing row of the same name is used; `link`: a metric is attached. */
    action: z.enum(["create", "reuse", "link", "skip"]),
    existing_id: Uuid.nullable(),
    /** The spec entry it belongs to (a seat's title for a responsibility, …), for display. */
    parent: z.string().nullable(),
    /** Why an entry is skipped, or a note on how it is applied. */
    reason: z.string().nullable(),
  })
  .meta({ id: "TemplatePlanItem" });

export const TemplateProblem = z
  .object({ path: z.string(), message: z.string() })
  .meta({ id: "TemplateProblem" });

/** Ids of everything the application created (also the `template.applied` payload). */
export const TemplateApplied = z
  .object({
    template_id: Uuid,
    version: z.int().min(1),
    created: z.object({
      roles: z.array(Uuid),
      teams: z.array(Uuid),
      seats: z.array(Uuid),
      responsibilities: z.array(Uuid),
      recurring_work: z.array(Uuid),
      spaces: z.array(Uuid),
      channels: z.array(Uuid),
      boards: z.array(Uuid),
      tasks: z.array(Uuid),
    }),
    /**
     * What the spec names but the application did not create (M1-06): spaces, channels, boards
     * and tasks while their module's handlers do not exist, and metric keys the company has no
     * definition for. Never in the event's `created`.
     */
    skipped: z.array(TemplatePlanItem),
  })
  .meta({ id: "TemplateApplied" });

/**
 * `POST templates/{id}/preview`: what `apply` with the same body would create, reuse, link or
 * skip, and why it would be refused (`problems`). Writes nothing.
 */
export const TemplatePreview = z
  .object({
    template_id: Uuid,
    version: z.int().min(1),
    valid: z.boolean(),
    problems: z.array(TemplateProblem),
    /** The parameters after defaults. */
    params: z.record(z.string(), z.string()),
    items: z.array(TemplatePlanItem),
  })
  .meta({ id: "TemplatePreview" });

const WebhookTypes = z.array(EventType).min(1).max(LIMITS.list);

export const WebhookCreate = z
  .strictObject({
    url: z.url({ protocol: /^https?$/ }).max(2048),
    description: FreeText.nullable().optional(),
    types: WebhookTypes,
    filter: WebhookFilter.optional(),
  })
  .meta({ id: "WebhookCreate" });
/** `status: active` re-enables a disabled hook. The secret is never changed here. */
export const WebhookPatch = WebhookCreate.extend({ status: WebhookStatus.optional() })
  .partial()
  .meta({ id: "WebhookPatch" });

/** The signing secret (`whsec_…`) is returned once, to the creator only. */
export const WebhookCreated = z
  .object({ webhook: Webhook, secret: z.string().startsWith("whsec_") })
  .meta({ id: "WebhookCreated" });

export const WebhookTestResult = z
  .object({
    delivery_id: Uuid,
    ok: z.boolean(),
    response_status: z.int().nullable(),
    error: z.string().nullable(),
  })
  .meta({ id: "WebhookTestResult" });

const TeamMemberParams = z.object({ co: Uuid, id: Uuid, seat_id: Uuid });
const WebhookDeliveryParams = z.object({ co: Uuid, id: Uuid, delivery_id: Uuid });

// Routes ----------------------------------------------------------------------------------------

const core = { feature: "core" } as const;

export const orgRoutes = {
  "companies.get": defineRoute({
    ...core,
    id: "companies.get",
    method: "get",
    path: base,
    tag: "companies",
    summary: "Get the company",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: CompanyDetail },
    errors: [],
    emits: [],
  }),
  "companies.update": defineRoute({
    ...core,
    id: "companies.update",
    method: "patch",
    path: base,
    tag: "companies",
    summary: "Update settings, calendar or currency",
    description:
      "HARDEN-mfa: `settings.security` (require two-factor sign-in for owners and admins) is " +
      "changed by an owner only (403 for any other manager); a patch whose `settings` omits it " +
      "keeps the stored value. REPORTS-UI: the Friday report opt-out list changes only with " +
      "`opted_out_expected` matching the stored list (409 `conflict` otherwise); without it a " +
      "differing list is ignored and the stored one kept (members set their own entry).",
    access: "manager",
    params: CompanyParams,
    body: CompanyPatch,
    response: { status: 200, schema: Company },
    errors: ["conflict"],
    emits: ["company.updated", "portfolio_projection.published"],
  }),

  "people.list": defineRoute({
    ...core,
    id: "people.list",
    method: "get",
    path: `${base}/people`,
    tag: "people",
    summary: "List people",
    description:
      "The company's people directory: managers and `company#member` (MEMBER-ROLE: a teammate " +
      "finds people to mention, message and assign). A client viewer is refused.",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Person) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "people.invite": defineRoute({
    ...core,
    id: "people.invite",
    method: "post",
    path: `${base}/people`,
    tag: "people",
    summary: "Invite a person (a pending membership; access only once accepted)",
    description:
      "Creates an invitation keyed by the normalized email, carrying the relations to grant. " +
      "No user, membership or tuple is written: the invitee accepts with the mailed token " +
      "while signed in with a verified email (`POST /v1/invitations/accept`). A pending " +
      "invitation of the same email is superseded.",
    access: "manager",
    params: CompanyParams,
    body: PersonInvite,
    idempotency: "optional",
    response: { status: 201, schema: Invitation },
    errors: ["conflict"],
    emits: ["invite.created"],
  }),
  "invitations.accept": defineRoute({
    ...core,
    id: "invitations.accept",
    method: "post",
    path: "/v1/invitations/accept",
    tag: "people",
    summary: "Accept an invitation (verified email of the invitee required)",
    description:
      "The signed-in user's email must be verified (a consumed magic link) and equal to the " +
      "invitation's. In one transaction the membership is created (or reactivated), the " +
      "invited relations are granted and the invitation is marked accepted.",
    access: "session",
    body: InvitationAccept,
    response: { status: 200, schema: InvitationAccepted },
    errors: ["invitation_invalid", "email_unverified", "forbidden"],
    emits: ["invite.accepted", "membership.created", "membership.updated", "access.granted"],
  }),
  "people.me": defineRoute({
    ...core,
    id: "people.me",
    method: "get",
    path: `${base}/people/me`,
    tag: "people",
    summary: "The caller's own membership in this company",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: Person },
    errors: [],
    emits: [],
  }),
  "people.update": defineRoute({
    ...core,
    id: "people.update",
    method: "patch",
    path: `${base}/people/{id}`,
    tag: "people",
    summary: "Change status, roles or display name",
    access: "manager",
    params: CompanyItemParams,
    body: PersonPatch,
    response: { status: 200, schema: Person },
    errors: [],
    emits: [
      "membership.updated",
      "membership.deactivated",
      "access.granted",
      "access.revoked",
      // STEP1-OUTBOX: a leaver's own connections' grants end
      "connection.grant_changed",
    ],
  }),
  "invitations.list": defineRoute({
    ...core,
    id: "invitations.list",
    method: "get",
    path: `${base}/invitations`,
    tag: "people",
    summary: "List pending invitations",
    description:
      "The company's invitations that are still `pending` and unexpired, oldest first. " +
      "The token is never returned.",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Invitation) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "invitations.revoke": defineRoute({
    ...core,
    id: "invitations.revoke",
    method: "delete",
    path: `${base}/invitations/{id}`,
    tag: "people",
    summary: "Withdraw a pending invitation (its token stops working)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: Invitation },
    errors: ["conflict"],
    emits: ["invite.revoked"],
  }),

  "roles.list": defineRoute({
    ...core,
    id: "roles.list",
    method: "get",
    path: `${base}/roles`,
    tag: "roles",
    summary: "List roles",
    description:
      "Every chart viewer (CHART-DIMENSIONS, founder 2026-10-21: roles are part of the org " +
      "chart's floor — seats, holders, titles, teams, reporting lines, roles and " +
      "responsibilities). Writes stay a manager's.",
    access: "viewer",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Role) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "roles.create": defineRoute({
    ...core,
    id: "roles.create",
    method: "post",
    path: `${base}/roles`,
    tag: "roles",
    summary: "Create a role",
    access: "manager",
    params: CompanyParams,
    body: RoleCreate,
    idempotency: "optional",
    response: { status: 201, schema: Role },
    errors: [],
    emits: ["role.created"],
  }),
  "roles.update": defineRoute({
    ...core,
    id: "roles.update",
    method: "patch",
    path: `${base}/roles/{id}`,
    tag: "roles",
    summary: "Update a role",
    access: "manager",
    params: CompanyItemParams,
    body: RolePatch,
    response: { status: 200, schema: Role },
    errors: [],
    emits: ["role.updated"],
  }),
  // ACTIONS: a role nothing uses is deleted; one a seat (open or closed), a role skill policy
  // or a skill pack names is refused (409) — seats keep their history, so a role never vanishes
  // from under one.
  "roles.delete": defineRoute({
    ...core,
    id: "roles.delete",
    method: "delete",
    path: `${base}/roles/{id}`,
    tag: "roles",
    summary: "Delete a role no seat, skill policy or skill pack uses (409 otherwise)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["role.deleted"],
  }),

  "seats.list": defineRoute({
    ...core,
    id: "seats.list",
    method: "get",
    path: `${base}/seats`,
    tag: "seats",
    summary: "List seats (filtered to those the caller can view)",
    access: "viewer",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Seat) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "seats.create": defineRoute({
    ...core,
    id: "seats.create",
    method: "post",
    path: `${base}/seats`,
    tag: "seats",
    summary: "Create a seat",
    access: "manager",
    params: CompanyParams,
    body: SeatCreate,
    idempotency: "optional",
    response: { status: 201, schema: Seat },
    errors: [],
    emits: ["seat.created"],
  }),
  "seats.get": defineRoute({
    ...core,
    id: "seats.get",
    method: "get",
    path: `${base}/seats/{id}`,
    tag: "seats",
    summary: "Get a seat with its active assignments",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: SeatDetail },
    errors: [],
    emits: [],
  }),
  "seats.update": defineRoute({
    ...core,
    id: "seats.update",
    method: "patch",
    path: `${base}/seats/{id}`,
    tag: "seats",
    summary: "Update a seat (If-Match)",
    access: "manager",
    params: CompanyItemParams,
    body: SeatPatch,
    ifMatch: true,
    response: { status: 200, schema: Seat },
    errors: ["seat_closed", "supervisor_required"],
    emits: ["seat.updated"],
  }),
  "seats.assign": defineRoute({
    ...core,
    id: "seats.assign",
    method: "post",
    path: `${base}/seats/{id}/assign`,
    tag: "seats",
    summary: "Assign a person or DE (one active of each kind; a DE needs a staffed supervisor)",
    access: "manager",
    params: CompanyItemParams,
    body: SeatAssign,
    idempotency: "optional",
    response: { status: 201, schema: SeatAssignment },
    errors: ["seat_occupied", "supervisor_required", "seat_closed"],
    emits: ["seat.assigned", "access.granted", "team.updated"],
  }),
  "seats.unassign": defineRoute({
    ...core,
    id: "seats.unassign",
    method: "post",
    path: `${base}/seats/{id}/unassign`,
    tag: "seats",
    summary: "End an assignment",
    access: "manager",
    params: CompanyItemParams,
    body: SeatUnassign,
    idempotency: "optional",
    response: { status: 200, schema: SeatAssignment },
    errors: [],
    emits: ["seat.unassigned", "access.revoked", "agent_key.revoked", "team.updated"],
  }),
  "seats.close": defineRoute({
    ...core,
    id: "seats.close",
    method: "post",
    path: `${base}/seats/{id}/close`,
    tag: "seats",
    summary: "Close a seat; ends assignments and revokes its keys in one transaction",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Seat },
    errors: ["seat_closed"],
    emits: [
      "seat.closed",
      "agent_key.revoked",
      "access.revoked",
      "team.updated",
      "recurring_work.updated",
    ],
  }),
  "seats.scorecard": defineRoute({
    feature: "kpi",
    id: "seats.scorecard",
    method: "get",
    path: `${base}/seats/{id}/scorecard`,
    tag: "scorecards",
    summary: "Seat scorecard for a period (computed on read)",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: ScorecardQuery,
    response: { status: 200, schema: Scorecard },
    errors: [],
    emits: [],
  }),
  "seats.cost": defineRoute({
    ...core,
    id: "seats.cost",
    method: "get",
    path: `${base}/seats/{id}/cost`,
    tag: "costs",
    summary: "Seat cost ledger and roll-up for a period (visibility by role)",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: SeatCostQuery,
    response: { status: 200, schema: SeatCost },
    errors: [],
    emits: [],
  }),
  "seats.metrics": defineRoute({
    feature: "kpi",
    id: "seats.metrics",
    method: "get",
    path: `${base}/seats/{id}/metrics`,
    tag: "metrics",
    summary: "The seat's metric set (every tracking mode) and its version (ETag)",
    access: "reader",
    params: CompanyItemParams,
    response: { status: 200, schema: SeatMetricSet },
    errors: [],
    emits: [],
  }),
  "seats.putMetrics": defineRoute({
    feature: "kpi",
    id: "seats.putMetrics",
    method: "put",
    path: `${base}/seats/{id}/metrics`,
    tag: "metrics",
    summary: "Replace the seat's metric set and weights (If-Match: the set's version)",
    access: "manager",
    params: CompanyItemParams,
    body: SeatMetricsPut,
    ifMatch: true,
    response: { status: 200, schema: SeatMetricSet },
    errors: ["seat_closed"],
    emits: ["seat_metric.changed"],
  }),
  // KPI-ASSIGN: one-row writes for the KPI assignment editor. They are versioned like the set
  // PUT (If-Match: the set's version from `seats.metrics`; 428 without it, 409 when stale) and
  // answer with the new set and its version, so the two editors never overwrite each other.
  "seats.putMetric": defineRoute({
    feature: "kpi",
    id: "seats.putMetric",
    method: "put",
    path: `${base}/seats/{id}/metrics/{metric_id}`,
    tag: "metrics",
    summary:
      "Add one metric to the seat or change its weights, leaving the rest of the set (If-Match)",
    access: "manager",
    params: SeatMetricParams,
    body: SeatMetricPut,
    ifMatch: true,
    response: { status: 200, schema: SeatMetricSet },
    errors: ["seat_closed"],
    emits: ["seat_metric.changed"],
  }),
  "seats.removeMetric": defineRoute({
    feature: "kpi",
    id: "seats.removeMetric",
    method: "delete",
    path: `${base}/seats/{id}/metrics/{metric_id}`,
    tag: "metrics",
    summary:
      "Take one metric off the seat (If-Match; idempotent; its observations and targets stay)",
    access: "manager",
    params: SeatMetricParams,
    ifMatch: true,
    response: { status: 200, schema: SeatMetricSet },
    errors: ["seat_closed"],
    emits: ["seat_metric.changed"],
  }),

  "digitalEmployees.list": defineRoute({
    ...core,
    id: "digitalEmployees.list",
    method: "get",
    path: `${base}/digital-employees`,
    tag: "digital-employees",
    summary: "List digital employees (removed ones with `include_removed=true`)",
    access: "viewer",
    params: CompanyParams,
    query: DigitalEmployeeListQuery,
    response: { status: 200, schema: page(DigitalEmployeeSummary) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "digitalEmployees.create": defineRoute({
    ...core,
    id: "digitalEmployees.create",
    method: "post",
    path: `${base}/digital-employees`,
    tag: "digital-employees",
    summary: "Create a DE: membership, digital_employee, assignment, template metrics and targets",
    access: "manager",
    params: CompanyParams,
    body: DigitalEmployeeCreate,
    idempotency: "optional",
    response: { status: 201, schema: DigitalEmployeeProfile },
    // de_allowance_reached: ONBOARDING-P3, the company's plan sets `included_des` and all are in use
    errors: [
      "seat_occupied",
      "supervisor_required",
      "seat_closed",
      "agent_mode_managed",
      "de_allowance_reached",
    ],
    emits: [
      "membership.created",
      "digital_employee.created",
      "seat.assigned",
      "access.granted",
      "seat_metric.changed",
      "target_rule.changed",
      "team.updated",
      // AGENT-RECORD: catalog entries an older company lacks (domain/org/catalog.ts)
      "metric_def.created",
      // AGENT-SPEC: the first version of the DE's agent spec, when the flow names one
      "agent_spec.versioned",
    ],
  }),
  "digitalEmployees.get": defineRoute({
    ...core,
    id: "digitalEmployees.get",
    method: "get",
    path: `${base}/digital-employees/{id}`,
    tag: "digital-employees",
    summary: "DE profile: seat, supervisor, scorecard summary, key metadata",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: DigitalEmployeeProfile },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.update": defineRoute({
    ...core,
    id: "digitalEmployees.update",
    method: "patch",
    path: `${base}/digital-employees/{id}`,
    tag: "digital-employees",
    summary:
      "Update a DE (status changes emit their own events). Managers change anything; a person above the DE in its reporting chain may pause, resume or rename it",
    access: "member",
    params: CompanyItemParams,
    body: DigitalEmployeePatch,
    response: { status: 200, schema: DigitalEmployeeProfile },
    // TEMPLATES-T4: activating a role DE whose required connections are not connected.
    // not_provisioned (lane A): activating an onboarding DE before its set-up and connection.
    errors: ["connections_missing", "not_provisioned"],
    emits: [
      "digital_employee.updated",
      "membership.updated",
      "digital_employee.activated",
      "digital_employee.paused",
      "digital_employee.resumed",
      "digital_employee.retired",
      // Retiring (terminal) ends the DE's seat assignments and revokes its live keys.
      "seat.unassigned",
      "access.revoked",
      "agent_key.revoked",
      "team.updated",
    ],
  }),
  "digitalEmployees.remove": defineRoute({
    ...core,
    id: "digitalEmployees.remove",
    method: "post",
    path: `${base}/digital-employees/{id}/remove`,
    tag: "digital-employees",
    summary:
      "Remove a DE: retire it, revoke every key, queue its LiteLLM keys for deletion, hide it from the roster",
    access: "manager",
    params: CompanyItemParams,
    body: DigitalEmployeeRemove,
    response: { status: 200, schema: DigitalEmployeeProfile },
    errors: ["validation_failed"],
    emits: [
      "digital_employee.removed",
      "digital_employee.retired",
      "membership.updated",
      "seat.unassigned",
      "access.revoked",
      "agent_key.revoked",
      "team.updated",
    ],
  }),
  // PURGE-RETIRED-AGENTS: a retired DE removed for good (owners and admins; never an agent).
  "digitalEmployees.purgePlan": defineRoute({
    ...core,
    id: "digitalEmployees.purgePlan",
    method: "get",
    path: `${base}/digital-employees/{id}/purge-plan`,
    tag: "digital-employees",
    summary:
      "What removing a retired DE permanently deletes, keeps (re-attributed) and does to its memory and tasks",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentPurgePlan },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.purge": defineRoute({
    ...core,
    id: "digitalEmployees.purge",
    method: "post",
    path: `${base}/digital-employees/{id}/purge`,
    tag: "digital-employees",
    summary:
      "Remove a retired DE permanently, its name typed: record, profile, seat history, keys and endpoints deleted; work and history kept as 'Removed agent (name)'",
    access: "manager",
    params: CompanyItemParams,
    body: AgentPurge,
    response: { status: 200, schema: AgentPurgeResult },
    errors: ["validation_failed", "conflict"],
    emits: [
      "agent.purged",
      "task.unassigned",
      "task.assigned",
      "run.cancelled",
      "budget.released",
      "budget.settled",
    ],
  }),
  "digitalEmployees.putHowIWork": defineRoute({
    ...core,
    id: "digitalEmployees.putHowIWork",
    method: "put",
    path: `${base}/digital-employees/{id}/how-i-work`,
    tag: "digital-employees",
    summary:
      "Save a new version of the DE's \"How I work\" (a company admin or owner, or the DE's supervisor); sent to the DE at its next run start",
    access: "member",
    params: CompanyItemParams,
    body: DeHowIWorkPut,
    response: { status: 200, schema: DeHowIWork },
    errors: ["conflict"],
    emits: ["digital_employee.updated"],
  }),
  "digitalEmployees.howIWorkVersions": defineRoute({
    ...core,
    id: "digitalEmployees.howIWorkVersions",
    method: "get",
    path: `${base}/digital-employees/{id}/how-i-work/versions`,
    tag: "digital-employees",
    summary:
      'The DE\'s "How I work" history: every saved version, who saved it and when, newest first',
    access: "seat_viewer",
    params: CompanyItemParams,
    query: DeHowIWorkVersionsQuery,
    response: {
      status: 200,
      schema: z
        .object({ data: z.array(DeHowIWorkVersion), next_cursor: z.string().nullable() })
        .meta({ id: "DeHowIWorkVersionPage" }),
    },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.standingOrders": defineRoute({
    ...core,
    id: "digitalEmployees.standingOrders",
    method: "get",
    path: `${base}/digital-employees/{id}/standing-orders`,
    tag: "digital-employees",
    summary: "The DE's standing orders as saved (the company's override) and as deployed",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: DeStandingOrders },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.job": defineRoute({
    ...core,
    id: "digitalEmployees.job",
    method: "get",
    path: `${base}/digital-employees/{id}/job`,
    tag: "digital-employees",
    summary:
      "The DE's job set-up: path, submitted, home channel, its seat's KPIs and which it reports, post approval",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: DeJobView },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.putJob": defineRoute({
    ...core,
    id: "digitalEmployees.putJob",
    method: "put",
    path: `${base}/digital-employees/{id}/job`,
    tag: "digital-employees",
    summary:
      "Save part of the DE's job set-up (KPIs and targets on its seat, reporting, home channel, post approval, submit)",
    access: "manager",
    params: CompanyItemParams,
    body: DeJobPut,
    response: { status: 200, schema: DeJobView },
    errors: ["validation_failed", "conflict"],
    emits: [
      "digital_employee.job_changed",
      "seat_metric.changed",
      "target_rule.changed",
      "seat.updated",
      "reporting_duty.changed",
    ],
  }),
  // TASK-BUDGETS (core plan item 8): one DE's per-task limits, from its seat settings.
  "digitalEmployees.taskLimits": defineRoute({
    ...core,
    id: "digitalEmployees.taskLimits",
    method: "get",
    path: `${base}/digital-employees/{id}/task-limits`,
    tag: "digital-employees",
    summary:
      "The DE's per-task limits: whether enforced, the company's DE defaults, its override and what a run gets",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: DeTaskLimitsView },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.putTaskLimits": defineRoute({
    ...core,
    id: "digitalEmployees.putTaskLimits",
    method: "put",
    path: `${base}/digital-employees/{id}/task-limits`,
    tag: "digital-employees",
    summary:
      "Set the DE's per-task limit override (steps, cost, time; only the fields to differ) or clear it; read at its next run's admission",
    access: "manager",
    params: CompanyItemParams,
    body: DeTaskLimitsPut,
    response: { status: 200, schema: DeTaskLimitsView },
    errors: ["conflict"],
    emits: ["digital_employee.updated"],
  }),
  "digitalEmployees.createKey": defineRoute({
    ...core,
    id: "digitalEmployees.createKey",
    method: "post",
    path: `${base}/digital-employees/{id}/keys`,
    tag: "digital-employees",
    summary: "Issue an ingest key; the plaintext is returned once",
    access: "manager",
    params: CompanyItemParams,
    body: AgentKeyCreate,
    idempotency: "optional",
    response: { status: 201, schema: AgentKeyCreated },
    errors: ["key_unbound"],
    emits: ["agent_key.created"],
  }),
  "digitalEmployees.revokeKey": defineRoute({
    ...core,
    id: "digitalEmployees.revokeKey",
    method: "delete",
    path: `${base}/digital-employees/{id}/keys/{kid}`,
    tag: "digital-employees",
    summary: "Revoke an ingest key",
    access: "manager",
    params: z.object({ co: Uuid, id: Uuid, kid: Uuid }),
    response: { status: 200, schema: AgentKey },
    errors: [],
    emits: ["agent_key.revoked"],
  }),
  "agentKeys.list": defineRoute({
    ...core,
    id: "agentKeys.list",
    method: "get",
    path: `${base}/agent-keys`,
    tag: "digital-employees",
    summary: "Every agent key of the company (prefix, kind, scopes, status), with its holder",
    description:
      "CONNECTIONS-AREA: Team › Connections › Agent keys. Managers. Ingest, agent and service " +
      "keys and source-scoped time keys; active first, then revoked. Never a secret or hash.",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(CompanyAgentKey) }) },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.scorecard": defineRoute({
    feature: "kpi",
    id: "digitalEmployees.scorecard",
    method: "get",
    path: `${base}/digital-employees/{id}/scorecard`,
    tag: "scorecards",
    summary: "Alias of the DE seat's scorecard",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: ScorecardQuery,
    response: { status: 200, schema: Scorecard },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.timeline": defineRoute({
    ...core,
    id: "digitalEmployees.timeline",
    method: "get",
    path: `${base}/digital-employees/{id}/timeline`,
    tag: "digital-employees",
    summary: "Activity and outcome events of the DE, newest first",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: TimelineQuery,
    response: {
      status: 200,
      schema: z.object({ data: z.array(TimelineItem), next_cursor: z.string().nullable() }),
    },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "digitalEmployees.createLitellmKey": defineRoute({
    ...core,
    id: "digitalEmployees.createLitellmKey",
    method: "post",
    path: `${base}/digital-employees/{id}/litellm-key`,
    tag: "digital-employees",
    summary:
      "Operator: start a provisioning attempt of the DE's own LiteLLM key (max_budget) and read key",
    description:
      "Reserves the attempt in the request transaction; the LiteLLM calls run after it commits, " +
      "outside any transaction, and a second short transaction finalizes (or the reconciler " +
      "does). The key is then issued, sealed, to the calling operator: collect it with GET and " +
      "acknowledge it with POST …/ack before it expires.",
    access: "operator",
    params: CompanyItemParams,
    body: LitellmKeyCreate,
    idempotency: "optional",
    response: { status: 200, schema: LitellmKeyResult },
    errors: ["conflict", "key_unbound"],
    emits: [
      "digital_employee.litellm_key_created",
      "digital_employee.provisioning_failed",
      // The puller's own ingest key (`ingest:cost` only), sealed with the read credential.
      "agent_key.created",
    ],
  }),
  "digitalEmployees.getLitellmKey": defineRoute({
    ...core,
    id: "digitalEmployees.getLitellmKey",
    method: "get",
    path: `${base}/digital-employees/{id}/litellm-key`,
    tag: "digital-employees",
    summary: "Operator: collect the issued LiteLLM key (its recipient only, until ack or expiry)",
    access: "operator",
    params: CompanyItemParams,
    response: { status: 200, schema: LitellmKeyIssuance },
    // PAUSED-GAPS: a paused, not deployed or retired DE's issued key is withheld.
    errors: ["conflict"],
    emits: [],
  }),
  "digitalEmployees.ackLitellmKey": defineRoute({
    ...core,
    id: "digitalEmployees.ackLitellmKey",
    method: "post",
    path: `${base}/digital-employees/{id}/litellm-key/ack`,
    tag: "digital-employees",
    summary: "Operator: acknowledge delivery of the issued key; a DE in setup becomes active",
    access: "operator",
    params: CompanyItemParams,
    body: LitellmKeyAck,
    response: { status: 200, schema: LitellmKeyResult },
    errors: ["conflict"],
    emits: ["digital_employee.litellm_key_delivered", "digital_employee.activated"],
  }),

  "externalIdentities.import": defineRoute({
    ...core,
    id: "externalIdentities.import",
    method: "post",
    path: `${base}/external-identities/import`,
    tag: "imports",
    summary: "CSV import of external id mappings (dry_run supported)",
    access: "manager",
    params: CompanyParams,
    body: ImportUpload,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 200, schema: ImportResult },
    errors: ["payload_too_large"],
    emits: ["external_identity.created", "external_identity.closed", "import.committed"],
  }),

  // M1-02 contracts (handlers: teams and org chart M1-04, responsibilities and recurring work
  // M1-05, templates M1-06, webhooks M1-07).
  "teams.list": defineRoute({
    ...core,
    id: "teams.list",
    method: "get",
    path: `${base}/teams`,
    tag: "teams",
    summary: "List teams (filtered to those the caller can view; 05 `team#viewer`)",
    access: "viewer",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Team) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "teams.create": defineRoute({
    ...core,
    id: "teams.create",
    method: "post",
    path: `${base}/teams`,
    tag: "teams",
    summary: "Create a team",
    access: "manager",
    params: CompanyParams,
    body: TeamCreate,
    idempotency: "optional",
    response: { status: 201, schema: Team },
    errors: ["conflict", "org_chart_cycle"],
    emits: ["team.created"],
  }),
  "teams.get": defineRoute({
    ...core,
    id: "teams.get",
    method: "get",
    path: `${base}/teams/{id}`,
    tag: "teams",
    summary: "Get a team with its member seats",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: TeamDetail },
    errors: [],
    emits: [],
  }),
  "teams.update": defineRoute({
    ...core,
    id: "teams.update",
    method: "patch",
    path: `${base}/teams/{id}`,
    tag: "teams",
    summary: "Rename, re-parent, or set the lead / human owner seat",
    access: "manager",
    params: CompanyItemParams,
    body: TeamPatch,
    response: { status: 200, schema: Team },
    errors: ["conflict", "org_chart_cycle"],
    emits: ["team.updated"],
  }),
  "teams.delete": defineRoute({
    ...core,
    id: "teams.delete",
    method: "delete",
    path: `${base}/teams/{id}`,
    tag: "teams",
    summary: "Delete a team (seats stay; child teams must be moved first)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["team.deleted", "workforce_agent.updated"],
  }),
  "teams.addMember": defineRoute({
    ...core,
    id: "teams.addMember",
    method: "post",
    path: `${base}/teams/{id}/members`,
    tag: "teams",
    summary: "Add a seat to the team",
    access: "manager",
    params: CompanyItemParams,
    body: TeamMemberAdd,
    idempotency: "optional",
    response: { status: 201, schema: TeamMember },
    errors: ["conflict", "seat_closed"],
    emits: ["team.member_changed", "team.updated"],
  }),
  "teams.removeMember": defineRoute({
    ...core,
    id: "teams.removeMember",
    method: "delete",
    path: `${base}/teams/{id}/members/{seat_id}`,
    tag: "teams",
    summary: "Remove a seat from the team",
    access: "manager",
    params: TeamMemberParams,
    response: { status: 204 },
    errors: [],
    emits: ["team.member_changed", "team.updated"],
  }),

  "orgChart.get": defineRoute({
    ...core,
    id: "orgChart.get",
    method: "get",
    path: `${base}/org-chart`,
    tag: "org-chart",
    summary: "Seats, edges and teams in one call (names and titles only)",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: OrgChart },
    errors: [],
    emits: [],
  }),
  "orgChart.putEdges": defineRoute({
    ...core,
    id: "orgChart.putEdges",
    method: "put",
    path: `${base}/org-chart/edges`,
    tag: "org-chart",
    summary: "Batch-replace edges (acyclic per kind, supervisor rule) in one transaction",
    access: "manager",
    params: CompanyParams,
    body: OrgChartEdgesPut,
    idempotency: "optional",
    response: { status: 200, schema: OrgChart },
    errors: ["org_chart_cycle", "supervisor_required", "seat_closed", "conflict"],
    emits: ["seat_edge.changed", "seat.updated"],
  }),
  "orgChart.putOrder": defineRoute({
    ...core,
    id: "orgChart.putOrder",
    method: "put",
    path: `${base}/org-chart/order`,
    tag: "org-chart",
    summary: "Order the seats under one supervisor (the chart left to right, the table top down)",
    access: "manager",
    params: CompanyParams,
    body: OrgChartOrderPut,
    idempotency: "optional",
    response: { status: 200, schema: OrgChart },
    errors: ["conflict"],
    emits: ["seat.updated"],
  }),
  "orgChart.dimensions": defineRoute({
    ...core,
    id: "orgChart.dimensions",
    method: "get",
    path: `${base}/org-chart/dimensions`,
    tag: "org-chart",
    summary: "What each seat card shows beyond holder and title, cut to the caller per dimension",
    access: "viewer",
    params: CompanyParams,
    query: OrgChartDimensionsQuery,
    response: { status: 200, schema: OrgChartDimensions },
    errors: [],
    emits: [],
  }),
  "orgChart.kpiVisibility": defineRoute({
    ...core,
    id: "orgChart.kpiVisibility",
    method: "get",
    path: `${base}/org-chart/kpi-visibility`,
    tag: "org-chart",
    summary: "The seats whose KPI data the caller may see (the chart's kpi audience)",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: OrgChartKpiVisibility },
    errors: [],
    emits: [],
  }),
  // KPI-VISIBILITY (05 §KPI visibility): Setup › People & access › KPI visibility (managers).
  "kpiVisibility.list": defineRoute({
    ...core,
    id: "kpiVisibility.list",
    method: "get",
    path: `${base}/kpi-visibility/grants`,
    tag: "kpi-visibility",
    summary: "The company's KPI visibility grants",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: KpiGrantList },
    errors: [],
    emits: [],
  }),
  "kpiVisibility.create": defineRoute({
    ...core,
    id: "kpiVisibility.create",
    method: "post",
    path: `${base}/kpi-visibility/grants`,
    tag: "kpi-visibility",
    summary: "Grant a job role, seat, team or person wider KPI visibility",
    access: "manager",
    params: CompanyParams,
    body: KpiGrantCreate,
    response: { status: 201, schema: KpiGrant },
    errors: [],
    emits: ["kpi_grant.changed"],
  }),
  "kpiVisibility.update": defineRoute({
    ...core,
    id: "kpiVisibility.update",
    method: "patch",
    path: `${base}/kpi-visibility/grants/{id}`,
    tag: "kpi-visibility",
    summary: "Change a grant's scopes or dimension groups",
    access: "manager",
    params: CompanyItemParams,
    body: KpiGrantPatch,
    response: { status: 200, schema: KpiGrant },
    errors: [],
    emits: ["kpi_grant.changed"],
  }),
  "kpiVisibility.delete": defineRoute({
    ...core,
    id: "kpiVisibility.delete",
    method: "delete",
    path: `${base}/kpi-visibility/grants/{id}`,
    tag: "kpi-visibility",
    summary: "Remove a KPI visibility grant",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["kpi_grant.changed"],
  }),
  "kpiVisibility.preview": defineRoute({
    ...core,
    id: "kpiVisibility.preview",
    method: "get",
    path: `${base}/kpi-visibility/preview`,
    tag: "kpi-visibility",
    summary: "The seats a person would see KPI data on, per dimension group",
    access: "manager",
    params: CompanyParams,
    query: KpiVisibilityPreviewQuery,
    response: { status: 200, schema: KpiVisibilityPreview },
    errors: [],
    emits: [],
  }),
  "orgChart.simulate": defineRoute({
    ...core,
    id: "orgChart.simulate",
    method: "post",
    path: `${base}/org-chart/simulate`,
    tag: "org-chart",
    summary: "What `PUT edges` with this body would change, and why it would be refused",
    access: "manager",
    params: CompanyParams,
    body: OrgChartEdgesPut,
    safe: true,
    response: { status: 200, schema: OrgChartSimulation },
    errors: [],
    emits: [],
  }),

  "responsibilities.list": defineRoute({
    ...core,
    id: "responsibilities.list",
    method: "get",
    path: `${base}/seats/{id}/responsibilities`,
    tag: "responsibilities",
    summary: "The seat's responsibilities",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(Responsibility) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "responsibilities.create": defineRoute({
    ...core,
    id: "responsibilities.create",
    method: "post",
    path: `${base}/seats/{id}/responsibilities`,
    tag: "responsibilities",
    summary: "Add a responsibility to the seat",
    access: "manager",
    params: CompanyItemParams,
    body: ResponsibilityCreate,
    idempotency: "optional",
    response: { status: 201, schema: Responsibility },
    errors: ["seat_closed"],
    emits: ["responsibility.created"],
  }),
  "responsibilities.update": defineRoute({
    ...core,
    id: "responsibilities.update",
    method: "patch",
    path: `${base}/responsibilities/{id}`,
    tag: "responsibilities",
    summary: "Change a responsibility (archiving pauses its recurring work)",
    access: "manager",
    params: CompanyItemParams,
    body: ResponsibilityPatch,
    response: { status: 200, schema: Responsibility },
    errors: [],
    emits: ["responsibility.updated", "recurring_work.updated"],
  }),
  "responsibilities.delete": defineRoute({
    ...core,
    id: "responsibilities.delete",
    method: "delete",
    path: `${base}/responsibilities/{id}`,
    tag: "responsibilities",
    summary: "Remove a responsibility and its recurring work",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["responsibility.removed"],
  }),
  // CHART-KB (04 §Knowledge, goals: `goals` CRUD; 05 `goal`: viewer = company#member, editor =
  // owner#holder | company#admin; the team-lead editor lands with M5's cadence screens). The
  // list is cut to the Keystones audience like `priorities.list` (403 outside it), the seat's
  // writers always read.
  "goals.list": defineRoute({
    ...core,
    id: "goals.list",
    method: "get",
    path: `${base}/seats/{id}/goals`,
    tag: "goals",
    summary:
      "The seat's goals (priorities first, newest first), with their milestones, for the Keystones audience and the seat's writers",
    access: "viewer",
    params: CompanyItemParams,
    query: GoalListQuery,
    response: { status: 200, schema: page(GoalDetail) },
    errors: [],
    emits: [],
  }),
  "goals.create": defineRoute({
    ...core,
    id: "goals.create",
    method: "post",
    path: `${base}/seats/{id}/goals`,
    tag: "goals",
    summary: "Add a goal (a quarterly priority by default) to the seat",
    access: "viewer",
    params: CompanyItemParams,
    body: GoalCreate,
    idempotency: "optional",
    response: { status: 201, schema: GoalDetail },
    errors: ["seat_closed"],
    emits: ["goal.created"],
  }),
  "goals.update": defineRoute({
    ...core,
    id: "goals.update",
    method: "patch",
    path: `${base}/goals/{id}`,
    tag: "goals",
    summary: "Change a goal, its track, its state or its milestones",
    access: "viewer",
    params: CompanyItemParams,
    body: GoalPatch,
    response: { status: 200, schema: GoalDetail },
    errors: [],
    emits: ["goal.updated", "goal.track_changed", "goal.milestone_done"],
  }),
  "goals.delete": defineRoute({
    ...core,
    id: "goals.delete",
    method: "delete",
    path: `${base}/goals/{id}`,
    tag: "goals",
    summary: "Remove a goal and its milestones",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["goal.deleted"],
  }),
  // PRIORITIES-HOME (06 §Cadence "Priorities"): the company's quarter of priorities, cut to the
  // Keystones audience, and the quarter roll-over (writers only: the holder's seats, or all for
  // a manager).
  "priorities.list": defineRoute({
    ...core,
    id: "priorities.list",
    method: "get",
    path: `${base}/priorities`,
    tag: "goals",
    summary: "The company's priorities of a quarter, by seat, cut to the Keystones audience",
    access: "viewer",
    params: CompanyParams,
    query: PriorityListQuery,
    response: { status: 200, schema: PriorityBoard },
    errors: [],
    emits: [],
  }),
  "priorities.carryOver": defineRoute({
    ...core,
    id: "priorities.carryOver",
    method: "post",
    path: `${base}/priorities/carry-over`,
    tag: "goals",
    summary: "Carry the unfinished priorities of a quarter into another",
    access: "viewer",
    params: CompanyParams,
    body: PriorityCarryOver,
    idempotency: "optional",
    response: { status: 200, schema: PriorityCarryOverResult },
    errors: [],
    emits: ["goal.created", "goal.updated"],
  }),
  "recurringWork.list": defineRoute({
    ...core,
    id: "recurringWork.list",
    method: "get",
    path: `${base}/responsibilities/{id}/recurring-work`,
    tag: "responsibilities",
    summary: "Schedules of a responsibility",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(RecurringWork) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "recurringWork.create": defineRoute({
    ...core,
    id: "recurringWork.create",
    method: "post",
    path: `${base}/responsibilities/{id}/recurring-work`,
    tag: "responsibilities",
    summary: "Add a schedule (RRULE) that materialises a task",
    access: "manager",
    params: CompanyItemParams,
    body: RecurringWorkCreate,
    idempotency: "optional",
    response: { status: 201, schema: RecurringWork },
    errors: ["schedule_invalid"],
    emits: ["recurring_work.created"],
  }),
  "recurringWork.update": defineRoute({
    ...core,
    id: "recurringWork.update",
    method: "patch",
    path: `${base}/recurring-work/{id}`,
    tag: "responsibilities",
    summary: "Change a schedule, its board or task template; pause or resume",
    access: "manager",
    params: CompanyItemParams,
    body: RecurringWorkPatch,
    response: { status: 200, schema: RecurringWork },
    errors: ["schedule_invalid"],
    emits: ["recurring_work.updated"],
  }),
  "recurringWork.delete": defineRoute({
    ...core,
    id: "recurringWork.delete",
    method: "delete",
    path: `${base}/recurring-work/{id}`,
    tag: "responsibilities",
    summary: "Remove a schedule",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["recurring_work.deleted"],
  }),
  "recurringWork.materialiseNow": defineRoute({
    ...core,
    id: "recurringWork.materialiseNow",
    method: "post",
    path: `${base}/recurring-work/{id}/materialise-now`,
    tag: "responsibilities",
    summary: "Materialise the next occurrence now (the schedule is not advanced twice)",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: RecurringWork },
    errors: ["conflict"],
    emits: ["recurring_work.materialised", "task.created", "page.created"],
  }),

  "templates.list": defineRoute({
    ...core,
    id: "templates.list",
    method: "get",
    path: `${base}/templates`,
    tag: "templates",
    summary: "List templates",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Template) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "templates.create": defineRoute({
    ...core,
    id: "templates.create",
    method: "post",
    path: `${base}/templates`,
    tag: "templates",
    summary: "Create a template",
    access: "manager",
    params: CompanyParams,
    body: TemplateCreate,
    idempotency: "optional",
    response: { status: 201, schema: Template },
    errors: ["conflict", "template_invalid"],
    emits: ["template.created"],
  }),
  "templates.get": defineRoute({
    ...core,
    id: "templates.get",
    method: "get",
    path: `${base}/templates/{id}`,
    tag: "templates",
    summary: "Get a template",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: Template },
    errors: [],
    emits: [],
  }),
  "templates.update": defineRoute({
    ...core,
    id: "templates.update",
    method: "patch",
    path: `${base}/templates/{id}`,
    tag: "templates",
    summary: "Rename or replace the spec (a new spec bumps version)",
    access: "manager",
    params: CompanyItemParams,
    body: TemplatePatch,
    response: { status: 200, schema: Template },
    errors: ["conflict", "template_invalid"],
    emits: ["template.updated"],
  }),
  "templates.delete": defineRoute({
    ...core,
    id: "templates.delete",
    method: "delete",
    path: `${base}/templates/{id}`,
    tag: "templates",
    summary: "Delete a template (what it created stays)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["template.deleted"],
  }),
  "templates.apply": defineRoute({
    ...core,
    id: "templates.apply",
    method: "post",
    path: `${base}/templates/{id}/apply`,
    tag: "templates",
    summary: "Apply in one transaction; returns the created ids",
    access: "manager",
    params: CompanyItemParams,
    body: TemplateApply,
    idempotency: "optional",
    response: { status: 201, schema: TemplateApplied },
    errors: ["template_invalid", "conflict"],
    emits: [
      "template.applied",
      "role.created",
      "team.created",
      "team.member_changed",
      "seat.created",
      "responsibility.created",
      "recurring_work.created",
      // M2-02 / M2-03's template handlers: spaces, channels, boards (blank: no fields), views,
      // starter tasks.
      "space.created",
      "channel.created",
      // CHAT-UX: a space's first channel is a default one, joined by the company's people.
      "channel.member_changed",
      "board.created",
      "view.created",
      "task.created",
      // M7-02: a starter task's description becomes its page.
      "page.created",
    ],
  }),
  "templates.preview": defineRoute({
    ...core,
    id: "templates.preview",
    method: "post",
    path: `${base}/templates/{id}/preview`,
    tag: "templates",
    summary: "What `apply` with this body would create, reuse or skip, and why it would refuse",
    access: "manager",
    params: CompanyItemParams,
    body: TemplateApply,
    safe: true,
    response: { status: 200, schema: TemplatePreview },
    errors: [],
    emits: [],
  }),

  "webhooks.list": defineRoute({
    ...core,
    id: "webhooks.list",
    method: "get",
    path: `${base}/webhooks`,
    tag: "webhooks",
    summary: "List outbound webhooks",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Webhook) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "webhooks.create": defineRoute({
    ...core,
    id: "webhooks.create",
    method: "post",
    path: `${base}/webhooks`,
    tag: "webhooks",
    summary: "Register a webhook; the signing secret is returned once",
    access: "manager",
    params: CompanyParams,
    body: WebhookCreate,
    idempotency: "optional",
    response: { status: 201, schema: WebhookCreated },
    errors: [],
    emits: ["webhook.created"],
  }),
  "webhooks.get": defineRoute({
    ...core,
    id: "webhooks.get",
    method: "get",
    path: `${base}/webhooks/{id}`,
    tag: "webhooks",
    summary: "Get a webhook (never its secret)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: Webhook },
    errors: [],
    emits: [],
  }),
  "webhooks.update": defineRoute({
    ...core,
    id: "webhooks.update",
    method: "patch",
    path: `${base}/webhooks/{id}`,
    tag: "webhooks",
    summary: "Change URL, types, filter; re-enable a disabled hook",
    access: "manager",
    params: CompanyItemParams,
    body: WebhookPatch,
    response: { status: 200, schema: Webhook },
    errors: [],
    emits: ["webhook.updated", "webhook.resumed"],
  }),
  // WOS-BUNDLE: the banner's Resume (Work OS `PATCH {active: true}` re-arms a paused hook).
  "webhooks.resume": defineRoute({
    ...core,
    id: "webhooks.resume",
    method: "post",
    path: `${base}/webhooks/{id}/resume`,
    tag: "webhooks",
    summary: "Resume a paused or disabled hook (its failure count starts again at 0)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: Webhook },
    errors: [],
    emits: ["webhook.resumed"],
  }),
  "webhooks.delete": defineRoute({
    ...core,
    id: "webhooks.delete",
    method: "delete",
    path: `${base}/webhooks/{id}`,
    tag: "webhooks",
    summary: "Remove a webhook and its delivery log",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["webhook.deleted"],
  }),
  "webhooks.test": defineRoute({
    ...core,
    id: "webhooks.test",
    method: "post",
    path: `${base}/webhooks/{id}/test`,
    tag: "webhooks",
    summary: "Send a signed test ping (`X-CompanyOS-Signature: t=,v1=`)",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: WebhookTestResult },
    errors: [],
    emits: ["webhook.tested"],
  }),
  "webhooks.rotateSecret": defineRoute({
    ...core,
    id: "webhooks.rotateSecret",
    method: "post",
    path: `${base}/webhooks/{id}/rotate-secret`,
    tag: "webhooks",
    summary: "Replace the signing secret; the new secret is returned once",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: WebhookCreated },
    errors: [],
    emits: ["webhook.secret_rotated"],
  }),
  "webhooks.retryDelivery": defineRoute({
    ...core,
    id: "webhooks.retryDelivery",
    method: "post",
    path: `${base}/webhooks/{id}/deliveries/{delivery_id}/retry`,
    tag: "webhooks",
    summary: "Send a failed or abandoned delivery again now",
    access: "manager",
    params: WebhookDeliveryParams,
    response: { status: 200, schema: WebhookDelivery },
    errors: ["conflict"],
    emits: ["webhook.delivery_retried"],
  }),
  "webhooks.deliveries": defineRoute({
    ...core,
    id: "webhooks.deliveries",
    method: "get",
    path: `${base}/webhooks/{id}/deliveries`,
    tag: "webhooks",
    summary: "Delivery log, newest first",
    access: "manager",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(WebhookDelivery) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
} as const;
